import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fToNow, fDateTime } from 'src/utils/format-time';

import { ICompanyListItem } from 'src/services/connections/connections.types';

import EntityAvatar from 'src/components/entity-avatar';
import { ColumnDef } from 'src/components/data-list/data-list.types';
import { renderChipList } from 'src/components/data-list/build-custom-field-columns';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

interface BuildCompanyColumnsArgs {
  t: Translate;
  /** Opens the record panel overlay (JT1-1529). The name becomes a button that opens it, not a hyperlink. */
  onOpenRecord: (companyId: string) => void;
  /** Columns for this org's custom fields, appended after the built-in ones. */
  customFieldColumns?: ColumnDef<ICompanyListItem>[];
}

/**
 * Companies list column registry (JT1-1528, item 27). Logo + name (opens the record panel), city,
 * number of jobs, number of contacts, owner, last activity. Counts come from the lean endpoint's
 * grouped aggregates; the LinkedIn icon (item 29 / JT1-1480) is a follow-up.
 */
export function buildCompanyColumns({ t, onOpenRecord, customFieldColumns = [] }: BuildCompanyColumnsArgs): ColumnDef<ICompanyListItem>[] {
  return [
    {
      key: 'name',
      label: t('companies.table.name', { defaultValue: 'Company' }),
      fixed: true,
      sortable: true,
      searchable: true,
      minWidth: 220,
      render: (row) => (
        <Stack direction="row" alignItems="center" gap={1}>
          <EntityAvatar src={row.logo_url} name={row.company_name} variant="rounded" size={28} fallbackIcon="solar:buildings-bold" />
          {/* Name opens the context-preserving record overlay (JT1-1529) — no longer a hyperlink. */}
          <Link component="button" type="button" onClick={() => onOpenRecord(row.id)} color="inherit" underline="hover" noWrap sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2">{row.company_name || '--'}</Typography>
          </Link>
        </Stack>
      ),
    },
    {
      key: 'city',
      label: t('companies.table.city', { defaultValue: 'Location' }),
      defaultVisible: true,
      sortable: true,
      searchable: true,
      render: (row) => (row.city ? <Typography variant="body2" color="text.secondary">{row.city}</Typography> : dash),
    },
    {
      key: 'jobs',
      label: t('companies.table.jobs', { defaultValue: 'Jobs' }),
      defaultVisible: true,
      sortable: true,
      align: 'right',
      render: (row) => <Typography variant="body2" color="text.secondary">{row.job_count}</Typography>,
    },
    {
      key: 'contacts',
      label: t('companies.table.contacts', { defaultValue: 'Contacts' }),
      defaultVisible: true,
      sortable: true,
      align: 'right',
      render: (row) => <Typography variant="body2" color="text.secondary">{row.contact_count}</Typography>,
    },
    {
      // Company labels were previously visible only on the company record, so the list could
      // neither show nor filter them. Shown by default: the whole point is that they were invisible,
      // and resolveColumns surfaces an unsaved key for users with an existing layout.
      key: 'labels',
      label: t('companies.table.labels', { defaultValue: 'Labels' }),
      defaultVisible: true,
      searchable: true,
      minWidth: 160,
      render: (row) => renderChipList(row.labels ?? []),
    },
    {
      key: 'account_owner',
      label: t('companies.table.owner', { defaultValue: 'Owner' }),
      defaultVisible: false,
      render: (row) => (row.account_owner_name ? <Typography variant="body2" color="text.secondary">{row.account_owner_name}</Typography> : dash),
    },
    {
      key: 'last_activity_at',
      label: t('companies.table.last_updated', { defaultValue: 'Last updated' }),
      defaultVisible: true,
      sortable: true,
      render: (row) =>
        row.last_activity_at ? (
          <Tooltip title={fToNow(row.last_activity_at)}>
            <Typography variant="body2" color="text.secondary">{fDateTime(row.last_activity_at)}</Typography>
          </Tooltip>
        ) : dash,
    },
    // Custom fields last: appending keeps every existing saved layout's relative order intact.
    ...customFieldColumns,
  ];
}
